import type { ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useTokens } from "../../hooks/useTokens";
import { corDaBoia } from "../../lib/cores";
import { dataHora, horaCurta, num } from "../../lib/format";
import { contarVazios, type PontoGrafico } from "../../lib/series";
import { ALTURA, Moldura, SemDados, corpoDica, type SerieDef } from "./comuns";

/** Grelha só horizontal, sólida e um tom acima da superfície. Nunca tracejada. */
function grelha(cor: string): ReactNode {
  return <CartesianGrid stroke={cor} strokeWidth={1} vertical={false} />;
}

function eixoX(cor: string, muted: string, janela: [number, number]): ReactNode {
  return (
    <XAxis
      dataKey="t"
      type="number"
      scale="time"
      // Domínio do filtro, não dos dados: dois gráficos lado a lado sob o
      // mesmo intervalo têm de ter o mesmo eixo, senão não se comparam.
      domain={janela}
      allowDataOverflow
      tickFormatter={(v: number) => horaCurta(new Date(v).toISOString())}
      stroke={cor}
      tick={{ fill: muted, fontSize: 10 }}
      tickLine={false}
      minTickGap={44}
      height={20}
    />
  );
}

type LimiteEixo = number | "auto" | ((extremo: number) => number);

/**
 * Limites do eixo y: fixos (bateria, 0–100), com folga à volta do mín/máx dos
 * dados, ou automáticos. A folga existe porque o ajuste automático estica
 * variações minúsculas até parecerem drama — ±0,02 °C a ocupar o gráfico todo.
 */
function dominioY(
  dominio?: [number, number],
  folga?: number,
  piso?: number,
): [LimiteEixo, LimiteEixo] {
  if (dominio !== undefined) return dominio;
  if (folga !== undefined) {
    // O piso corta a folga onde a grandeza acaba: uma altura de onda com
    // eixo a −0,3 m lia-se como medição possível, não como margem visual.
    return [
      (minimo: number) => (piso === undefined ? minimo - folga : Math.max(piso, minimo - folga)),
      (maximo: number) => maximo + folga,
    ];
  }
  return ["auto", "auto"];
}

function eixoY(
  t: ReturnType<typeof useTokens>,
  casas: number,
  dominio: [LimiteEixo, LimiteEixo] = ["auto", "auto"],
): ReactNode {
  return (
    <YAxis
      domain={dominio}
      tickFormatter={(v: number) => num(v, casas === 0 ? 0 : 1)}
      stroke={t["--axis"]}
      tick={{ fill: t["--text-muted"], fontSize: 10 }}
      tickLine={false}
      width={46}
    />
  );
}

// ---------------------------------------------------------------------------

export interface SerieBoia {
  code: string;
  pontos: PontoGrafico[];
}

interface BandaProps {
  titulo: string;
  unidade: string;
  /** Uma entrada por boia. A primeira é a do painel. */
  boias: SerieBoia[];
  casas: number;
  /** Intervalo do filtro, em epoch ms. */
  janela: [number, number];
  /** Folga do eixo y além do mín/máx dos dados, na unidade do gráfico. */
  folga?: number | undefined;
  /** Explica o que um buraco quer dizer nesta grandeza. */
  notaVazios?: ((vazios: number, total: number) => string) | undefined;
}

/** Junta as grelhas das várias boias num registo por instante. */
function fundir(boias: SerieBoia[]): Array<Record<string, number | null> & { t: number }> {
  const por = new Map<number, Record<string, number | null> & { t: number }>();
  for (const boia of boias) {
    for (const p of boia.pontos) {
      let linha = por.get(p.t);
      if (linha === undefined) {
        linha = { t: p.t };
        por.set(p.t, linha);
      }
      linha[boia.code] = p.mean;
    }
  }
  return [...por.values()].sort((a, b) => a.t - b.t);
}

/**
 * Temperatura e sonar.
 *
 * **Uma boia**: média em linha e mín–máx em banda. A banda importa — a média
 * sozinha esconde o pico de ondulação e o instante em que o sonar perdeu o eco,
 * que são precisamente os momentos que interessam.
 *
 * **Duas ou mais**: só as médias, uma linha por boia. Seis bandas sobrepostas
 * são uma mancha, e a razão de comparar boias é o *gradiente* entre elas — que
 * se lê nas médias.
 */
export function GraficoBanda({
  titulo,
  unidade,
  boias,
  casas,
  janela,
  folga,
  notaVazios,
}: BandaProps): ReactNode {
  const t = useTokens();
  const escuro = t["--modo"] === "dark";
  const comparar = boias.length > 1;
  const principal = boias[0];

  if (principal === undefined) return null;

  const cores: Record<string, string> = {};
  for (const b of boias) cores[b.code] = corDaBoia(b.code, escuro);

  const series: SerieDef[] = comparar
    ? boias.map((b) => ({ chave: b.code, rotulo: b.code, cor: cores[b.code]! }))
    : [{ chave: "mean", rotulo: "média", cor: cores[principal.code]! }];

  const vazios = contarVazios(principal.pontos);
  const temDados = boias.some((b) => contarVazios(b.pontos) < b.pontos.length);

  const tabela = comparar
    ? {
        cabecalho: ["Instante", ...boias.map((b) => b.code)],
        linhas: fundir(boias).map((linha) => [
          dataHora(new Date(linha.t).toISOString()),
          ...boias.map((b) => {
            const v = linha[b.code];
            return v === null || v === undefined ? null : num(v, casas);
          }),
        ]),
      }
    : {
        cabecalho: ["Instante", "Média", "Mín", "Máx", "N"],
        linhas: principal.pontos.map((p) => [
          dataHora(new Date(p.t).toISOString()),
          p.mean === null ? null : num(p.mean, casas),
          p.faixa === null ? null : num(p.faixa[0], casas),
          p.faixa === null ? null : num(p.faixa[1], casas),
          String(p.count),
        ]),
      };

  return (
    <Moldura
      titulo={titulo}
      unidade={unidade}
      nota={
        !comparar && vazios > 0 && notaVazios ? notaVazios(vazios, principal.pontos.length) : undefined
      }
      series={series}
      tabela={tabela}
    >
      {!temDados ? (
        <SemDados />
      ) : (
        <ResponsiveContainer width="100%" height={ALTURA}>
          <ComposedChart
            data={comparar ? fundir(boias) : principal.pontos}
            margin={{ top: 6, right: 8, bottom: 0, left: -12 }}
          >
            {grelha(t["--grid"])}
            {eixoX(t["--axis"], t["--text-muted"], janela)}
            {eixoY(t, casas, dominioY(undefined, folga))}
            <Tooltip
              cursor={{ stroke: t["--axis"], strokeWidth: 1 }}
              isAnimationActive={false}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload as Record<string, unknown> | undefined;
                if (p === undefined) return null;
                const itens = comparar
                  ? boias.map((b) => ({
                      cor: cores[b.code]!,
                      rotulo: b.code,
                      valor: (p[b.code] as number | null | undefined) ?? null,
                    }))
                  : [
                      { cor: series[0]!.cor, rotulo: "média", valor: (p["mean"] as number | null) },
                      {
                        cor: series[0]!.cor,
                        rotulo: "mín",
                        valor: (p["faixa"] as [number, number] | null)?.[0] ?? null,
                      },
                      {
                        cor: series[0]!.cor,
                        rotulo: "máx",
                        valor: (p["faixa"] as [number, number] | null)?.[1] ?? null,
                      },
                    ];
                return corpoDica(p["t"] as number, itens, unidade, casas);
              }}
            />

            {/* Banda mín–máx: só com uma boia. Mesma cor da série, recuada. */}
            {!comparar && (
              <Area
                dataKey="faixa"
                stroke="none"
                fill={series[0]!.cor}
                fillOpacity={0.16}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={false}
              />
            )}

            {series.map((s) => (
              <Line
                key={s.chave}
                dataKey={s.chave}
                name={s.rotulo}
                stroke={s.cor}
                strokeWidth={2}
                dot={false}
                // Buracos ficam buracos. Ligar por cima seria inventar dados.
                connectNulls={false}
                isAnimationActive={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: t["--surface"] }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Moldura>
  );
}

// ---------------------------------------------------------------------------

interface LinhasProps {
  titulo: string;
  unidade: string;
  /** Cada registo traz `t` e uma chave por série. */
  dados: Array<Record<string, number | null> & { t: number }>;
  series: Array<{ chave: string; rotulo: string; slot: 1 | 2 }>;
  casas: number;
  janela: [number, number];
  /** Limites fixos do eixo y (bateria: 0–100). Tem precedência sobre `folga`. */
  dominio?: [number, number] | undefined;
  /** Folga do eixo y além do mín/máx dos dados, na unidade do gráfico. */
  folga?: number | undefined;
  /** Limite inferior físico da grandeza: a folga nunca desce abaixo dele. */
  piso?: number | undefined;
  /** De que boia são estes dados. Obrigatório — ver `Moldura`. */
  boia: string;
  nota?: string | undefined;
}

/**
 * Ondulação e tensão: séries **de dentro** de uma boia, não entre boias.
 *
 * Por isso mantêm os dois slots fixos do CSS em vez das cores por boia — aqui a
 * cor distingue pitch de roll, não N1 de N3. E por isso o título leva o código
 * da boia: com outras boias em comparação nos gráficos de cima, sem ele estes
 * dois mostravam uma boia sem dizer qual.
 */
export function GraficoLinhas({
  titulo,
  unidade,
  dados,
  series,
  casas,
  janela,
  dominio,
  folga,
  piso,
  boia,
  nota,
}: LinhasProps): ReactNode {
  const t = useTokens();

  // Âncoras sem valor nas pontas da janela.
  //
  // O `domain` do eixo sozinho não chega — o Recharts volta a ajustar-se à
  // extensão dos dados. Sem isto, um gráfico cujos dados começam a meio do
  // intervalo desenhava um eixo x diferente do gráfico ao lado, sob o mesmo
  // filtro, e os dois deixavam de se poder comparar. As âncoras não desenham
  // nada: são pontos nulos, e a linha não os liga.
  const comAncoras = [
    { t: janela[0] },
    ...dados.filter((d) => d.t > janela[0] && d.t < janela[1]),
    { t: janela[1] },
  ] as LinhasProps["dados"];

  // Ordem fixa de slots. Nunca atribuída por posição no ranking nem ciclada.
  const cores: Record<1 | 2, string> = { 1: t["--series-1"], 2: t["--series-2"] };
  const definicoes: SerieDef[] = series.map((s) => ({
    chave: s.chave,
    rotulo: s.rotulo,
    cor: cores[s.slot],
  }));

  const tabela = {
    cabecalho: ["Instante", ...series.map((s) => s.rotulo)],
    linhas: dados.map((d) => [
      dataHora(new Date(d.t).toISOString()),
      ...series.map((s) => {
        const v = d[s.chave];
        return v === null || v === undefined ? null : num(v, casas);
      }),
    ]),
  };

  return (
    <Moldura
      titulo={titulo}
      unidade={unidade}
      boia={boia}
      nota={nota}
      series={definicoes}
      tabela={tabela}
    >
      {dados.length === 0 ? (
        <SemDados />
      ) : (
        <ResponsiveContainer width="100%" height={ALTURA}>
          <LineChart data={comAncoras} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
            {grelha(t["--grid"])}
            {eixoX(t["--axis"], t["--text-muted"], janela)}
            {eixoY(t, casas, dominioY(dominio, folga, piso))}
            <Tooltip
              cursor={{ stroke: t["--axis"], strokeWidth: 1 }}
              isAnimationActive={false}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload as
                  | (Record<string, number | null> & { t: number })
                  | undefined;
                if (!p) return null;
                return corpoDica(
                  p.t,
                  definicoes.map((d) => ({
                    cor: d.cor,
                    rotulo: d.rotulo,
                    valor: p[d.chave] ?? null,
                  })),
                  unidade,
                  casas,
                );
              }}
            />
            {definicoes.map((d) => (
              <Line
                key={d.chave}
                dataKey={d.chave}
                name={d.rotulo}
                stroke={d.cor}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: t["--surface"] }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Moldura>
  );
}
